/*
 * JS del tema MUS. Web components vanilla, sin dependencias: todo lo que
 * hacen estos componentes también funciona sin JS (formularios normales);
 * esto solo lo hace más rápido y sin recargar la página.
 *
 * - product-form (.product-form, en la ficha y en las tarjetas): agrega al carrito
 *   por AJAX sin salir de la página (la tapa vuela al carrito y el contador suma)
 *   o va directo al pago con «Comprar ahora».
 * - <cart-drawer> / <cart-items>: drawer y cambios de cantidad por Section Rendering API.
 * - <cart-toast>: aviso cuando no se pudo agregar desde una tarjeta (sin stock, error).
 * - <sticky-buy>: barra de compra fija en mobile cuando el botón sale de pantalla.
 * - <scroll-header>: al scrollear hacia arriba, en celular aparece una barra compacta
 *   (buscador, filtrar, carrito y la fila de páginas) que al volver arriba se desvanece
 *   en el lugar sobre el header; en desktop vuelve el header mismo (sticky).
 * - <predictive-search>: sugerencias mientras se escribe.
 * - <facet-filters>: abre el panel de filtros y envía el orden al cambiar.
 * - <load-more>: en listados, carga sola las próximas páginas al llegar abajo
 *   y después ofrece «Cargar más» (sin JS, el botón es un link a la página siguiente).
 * - <product-recommendations>: carga las recomendaciones después de la ficha.
 * - <product-lightbox>: fotos de la ficha en grande, a pantalla completa.
 * - <share-button>: compartir o copiar el link.
 */

const routes = window.Shopify && window.Shopify.routes ? window.Shopify.routes : { root: '/' };
const root = routes.root || '/';

function sectionHTML(html, selector) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const node = doc.querySelector(selector);
  return node ? node.innerHTML : '';
}

// Todos los links al carrito con contador (header y barra compacta):
// <a data-cart-link data-cart-badge-class="…"> con <span data-cart-badge>.
// Si el número sube, el contador da un saltito (.is-bumped, en critical.css).
function updateCartCount(count) {
  document.querySelectorAll('[data-cart-link]').forEach((link) => {
    let badge = link.querySelector('[data-cart-badge]');
    if (count > 0) {
      const previous = badge ? Number(badge.textContent) : 0;
      if (!badge) {
        badge = document.createElement('span');
        badge.className = link.dataset.cartBadgeClass;
        badge.dataset.cartBadge = '';
        link.append(badge);
      }
      badge.textContent = count;
      if (count > previous) {
        badge.classList.remove('is-bumped');
        void badge.offsetWidth;
        badge.classList.add('is-bumped');
      }
    } else if (badge) {
      badge.remove();
    }
  });
}

/* ---------- Drawer del carrito ---------- */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-close]') || event.target === this.dialog) this.close();
    });
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-cart-link], [data-open-cart]');
      if (!link) return;
      event.preventDefault();
      this.open();
    });
  }

  open() {
    const toast = document.querySelector('cart-toast');
    if (toast) toast.hide();
    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    this.dialog.close();
  }

  // Devuelve la cantidad de productos del carrito (o null si no vino la sección).
  // Con updateCount = false no toca el contador (lo actualiza quien llama, p. ej.
  // cuando la tapa llega al carrito).
  render(sections, updateCount = true) {
    const html = sections && sections['cart-drawer'];
    if (!html) return null;
    const body = this.querySelector('cart-items');
    body.innerHTML = sectionHTML(html, 'cart-items');
    const cart = body.querySelector('[data-cart-count]');
    if (!cart) return null;
    const count = Number(cart.dataset.cartCount);
    if (updateCount) updateCartCount(count);
    return count;
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------- Aviso del carrito ---------- */

// Hoy solo para cuando no se pudo agregar desde una tarjeta (lo que sale bien
// lo cuenta la tapa volando al carrito). Se va solo a los 5 s (se pausa con el mouse o el foco encima) y en celular
// se descarta deslizándolo hacia un costado o hacia abajo.
class CartToast extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.cart-toast__panel');
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-toast-close]')) this.hide();
    });
    this.addEventListener('pointerenter', () => this.pause());
    this.addEventListener('pointerleave', () => this.schedule());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.schedule());
    this.addEventListener('touchstart', (event) => this.dragStart(event), { passive: true });
    this.addEventListener('touchmove', (event) => this.dragMove(event), { passive: true });
    this.addEventListener('touchend', () => this.dragEnd());
  }

  show({ heading = 'Agregado al carrito', title, image, count }) {
    this.querySelector('[data-toast-heading]').textContent = heading;
    const cover = this.querySelector('[data-toast-cover]');
    if (image) {
      cover.src = image + (image.includes('?') ? '&' : '?') + 'width=120';
      cover.hidden = false;
    } else {
      cover.hidden = true;
    }
    this.querySelector('[data-toast-name]').textContent = title || '';
    this.querySelector('[data-toast-count]').textContent =
      count > 0 ? `${count} ${count === 1 ? 'producto' : 'productos'} en tu carrito` : '';
    this.panel.style.transform = '';
    this.panel.style.opacity = '';
    this.classList.add('is-visible');
    document.documentElement.classList.add('has-cart-toast');
    this.schedule();
  }

  hide() {
    this.pause();
    if (!this.classList.contains('is-visible')) return;
    this.classList.remove('is-visible');
    document.documentElement.classList.remove('has-cart-toast');
    // Si se descartó deslizando, el panel queda donde se soltó mientras se desvanece.
    window.setTimeout(() => {
      this.panel.style.transform = '';
      this.panel.style.opacity = '';
    }, 400);
  }

  schedule() {
    this.pause();
    if (this.classList.contains('is-visible')) this.timer = window.setTimeout(() => this.hide(), 5000);
  }

  pause() {
    window.clearTimeout(this.timer);
  }

  dragStart(event) {
    const touch = event.touches[0];
    this.start = [touch.clientX, touch.clientY];
    this.drag = [0, 0];
    this.pause();
  }

  dragMove(event) {
    if (!this.start) return;
    const touch = event.touches[0];
    this.drag = [touch.clientX - this.start[0], Math.max(0, touch.clientY - this.start[1])];
    this.panel.style.transition = 'none';
    this.panel.style.transform = `translate(${this.drag[0]}px, ${this.drag[1]}px)`;
    this.panel.style.opacity = String(1 - Math.min(1, Math.hypot(...this.drag) / 200));
  }

  dragEnd() {
    if (!this.start) return;
    const [dx, dy] = this.drag;
    this.start = null;
    this.panel.style.transition = '';
    if (Math.abs(dx) > 60 || dy > 40) {
      this.panel.style.opacity = '0';
      this.hide();
    } else {
      this.panel.style.transform = '';
      this.panel.style.opacity = '';
      this.schedule();
    }
  }
}
customElements.define('cart-toast', CartToast);

/* ---------- Cambios de cantidad (página /cart y drawer) ---------- */

class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-line-key]');
      if (input && input.matches('input')) this.change(input.dataset.lineKey, Number(input.value));
    });
    this.addEventListener('click', (event) => {
      // «− 1 +»: el paso cambia el campo y se guarda como cualquier cambio
      const step = event.target.closest('[data-qty-step]');
      if (step) {
        const input = step.parentElement.querySelector('input[data-line-key]');
        const quantity = Math.max(0, Number(input.value) + Number(step.dataset.qtyStep));
        // Con stock limitado, avisamos sin pedirle nada a Shopify
        if (input.dataset.max && quantity > Number(input.dataset.max) && quantity > Number(input.dataset.quantity)) {
          this.notify(input.dataset.lineKey, stockMessage(input.dataset.max));
          return;
        }
        input.value = quantity;
        this.change(input.dataset.lineKey, quantity);
        return;
      }
      const remove = event.target.closest('[data-remove]');
      if (!remove) return;
      event.preventDefault();
      this.change(remove.dataset.lineKey, 0);
    });
  }

  lineFor(key) {
    const input = [...this.querySelectorAll('input[data-line-key]')].find((el) => el.dataset.lineKey === key);
    return input ? input.closest('.cart-line') : null;
  }

  // Mensaje en la línea (queda hasta el próximo cambio, que re-renderiza el carrito)
  notify(key, message) {
    const line = this.lineFor(key);
    const notice = line && line.querySelector('[data-line-notice]');
    if (notice) notice.textContent = message;
  }

  async change(key, quantity) {
    const section = this.dataset.section;
    const cart = this.querySelector('.cart');
    if (cart) cart.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [section], sections_url: window.location.pathname }),
      });
      const state = await response.json().catch(() => ({}));
      if (!response.ok) throw Object.assign(new Error(state.description || state.message), { status: response.status });
      if (state.sections && state.sections[section]) {
        this.innerHTML = sectionHTML(state.sections[section], 'cart-items');
      }
      updateCartCount(state.item_count);
    } catch (error) {
      // Sin stock (422) u otro error: la cantidad vuelve a la que había y se
      // explica en la línea, sin recargar (en el drawer, recargar lo cerraba).
      const line = this.lineFor(key);
      const input = line && line.querySelector('input[data-line-key]');
      if (input) input.value = input.dataset.quantity;
      this.notify(
        key,
        error.status === 422
          ? stockMessage(input && input.dataset.max)
          : 'No pudimos actualizar el carrito. Probá de nuevo en un momento.'
      );
      if (cart) cart.removeAttribute('aria-busy');
    }
  }
}

function stockMessage(max) {
  const units = Number(max);
  if (units === 1) return 'Es la última unidad que tenemos en stock.';
  if (units > 1) return `Tenemos ${units} unidades en stock y ya están todas en tu carrito.`;
  return 'No tenemos más unidades en stock.';
}
customElements.define('cart-items', CartItems);

/* ---------- Agregar al carrito ---------- */

// Agregar deja al visitante donde está (para que siga explorando), en la ficha
// y en las tarjetas por igual: el botón confirma con «✓ Agregado» (o «✓» en el
// botón redondo de la tarjeta), la tapa vuela al carrito que se ve y, al
// llegar, el contador suma. Si algo falla, el mensaje va en la ficha o, desde
// una tarjeta, en el aviso. «Comprar ahora» (data-buy-now) agrega y va directo
// al pago, con lo que ya hubiera en el carrito.

function confirmAdded(button) {
  if (!button.dataset.label) button.dataset.label = button.innerHTML;
  window.clearTimeout(button.addedTimer);
  button.classList.add('is-added');
  button.textContent = button.dataset.addedText || '✓ Agregado';
  button.addedTimer = window.setTimeout(() => {
    button.classList.remove('is-added');
    button.innerHTML = button.dataset.label;
  }, 2000);
}

// Se agrega en el click del botón, que con preventDefault evita el envío del
// formulario: un submit a /cart/add hace que Shopify emita un
// product_added_to_cart propio («add-form»), además del de la llamada AJAX
// («add-fetch»). Eran dos agregados por cada uno en las analíticas (GA, Meta) y,
// en los listados, el de «add-form» llega sin datos del producto y rompía el
// pixel de Clarity. Enter en la cantidad también pasa por acá (el navegador lo
// convierte en click del botón). El submit queda para envíos que no vienen de
// un botón.
document.addEventListener('click', (event) => {
  const submitter = event.target.closest('[data-add-button], [data-buy-now]');
  const form = submitter && submitter.form;
  if (!form || !form.matches('.product-form') || !document.querySelector('cart-drawer')) return;
  event.preventDefault();
  addToCart(form, submitter);
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest('form.product-form');
  if (!form || !document.querySelector('cart-drawer')) return;
  event.preventDefault();
  addToCart(form, event.submitter);
});

async function addToCart(form, submitter) {
  const drawer = document.querySelector('cart-drawer');
  const buyNow = Boolean(submitter && submitter.hasAttribute('data-buy-now'));
  const button =
    submitter && submitter.matches('[data-add-button], [data-buy-now]') ? submitter : form.querySelector('[data-add-button]');
  const error = form.querySelector('[data-form-error]');
  const data = new FormData(form);
  if (!buyNow) {
    data.append('sections', 'cart-drawer');
    data.append('sections_url', window.location.pathname);
  }

  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  if (error) error.hidden = true;
  let leaving = false;

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: data,
    });
    // Si Shopify responde con una página (demasiados intentos, verificación),
    // no hay JSON: mostramos un mensaje entendible en vez del error técnico.
    const state = await response.json().catch(() => ({}));
    if (response.status === 422) throw new Error('Ya tenés en el carrito todas las unidades que tenemos en stock.');
    if (!response.ok || !state.id) {
      throw new Error(state.description || state.message || 'No pudimos agregarlo. Probá de nuevo en un momento.');
    }
    if (buyNow) {
      leaving = true;
      window.location.href = submitter.value || `${root}checkout`;
      return;
    }
    const count = drawer.render(state.sections, false);
    confirmAdded(button);
    const title = form.querySelector('[data-toast-title]');
    announce(`${title ? title.textContent.trim() : state.product_title}: agregado al carrito.`);
    flyToCart(form, button, () => {
      if (count !== null) updateCartCount(count);
    });
  } catch (err) {
    const toast = document.querySelector('cart-toast');
    if (error) {
      error.textContent = err.message;
      error.hidden = false;
    } else if (toast) {
      const title = form.querySelector('[data-toast-title]');
      const name = title ? `${title.textContent.trim()}. ` : '';
      toast.show({ heading: 'No se pudo agregar', title: name + err.message, image: null, count: 0 });
    }
  } finally {
    if (!leaving) {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }
}

// Para lectores de pantalla (el aviso visual es la tapa volando).
function announce(text) {
  const status = document.querySelector('[data-cart-status]');
  if (!status) return;
  status.textContent = '';
  window.setTimeout(() => (status.textContent = text), 100);
}

// La tapa vuela al carrito que se ve: el del header si está a la vista; si no,
// el de la barra compacta, que baja para recibirla. Al llegar, done() (el
// contador suma). Con movimiento reducido no vuela: done() enseguida.
function flyToCart(form, button, done) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const target = cartTarget();
  const source = !reduce && target && Element.prototype.animate ? flySource(form, button) : null;
  if (!source) {
    done();
    return;
  }
  const from = source.rect;
  const fly = document.createElement('img');
  fly.className = 'cart-fly';
  fly.src = source.src;
  fly.alt = '';
  Object.assign(fly.style, { left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px` });
  document.body.append(fly);

  const dx = target.left + target.width / 2 - (from.left + from.width / 2);
  const dy = target.top + target.height / 2 - (from.top + from.height / 2);
  const end = 22 / Math.max(from.width, from.height);
  const mid = Math.min(1, Math.max(end, 72 / Math.max(from.width, from.height)));
  const animation = fly.animate(
    [
      { transform: 'translate(0, 0) scale(1)', opacity: 1 },
      { transform: `translate(${dx * 0.3}px, ${dy * 0.3 - 24}px) scale(${mid})`, opacity: 1, offset: 0.35 },
      { transform: `translate(${dx}px, ${dy}px) scale(${end})`, opacity: 0.5 },
    ],
    { duration: 650, easing: 'cubic-bezier(0.5, 0, 0.25, 1)', fill: 'forwards' }
  );
  animation.finished
    .catch(() => {})
    .then(() => {
      fly.remove();
      done();
    });
}

function cartTarget() {
  const bar = document.querySelector('scroll-header');
  if (bar) return bar.cartTarget();
  const headerCart = document.querySelector('.site-header__cart');
  return headerCart ? headerCart.getBoundingClientRect() : null;
}

// Desde dónde sale: la tapa de la tarjeta o la foto de la ficha que se ve. Si
// la foto no está a la vista (p. ej. «Agregar» en la barra fija de abajo), sale
// una miniatura desde el botón.
function flySource(form, button) {
  const card = form.closest('.card-product');
  const images = card
    ? [card.querySelector('.card-product__cover img')]
    : [...document.querySelectorAll('.product__media img')].filter((img) => !img.closest('[data-gallery-thumb]'));
  const inView = (rect) =>
    rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight && rect.left > -1 && rect.right < window.innerWidth + 1;
  const visible = images.find((img) => img && inView(img.getBoundingClientRect()));
  if (visible) return { src: visible.currentSrc || visible.src, rect: visible.getBoundingClientRect() };
  const first = images.find(Boolean);
  if (!first) return null;
  const from = button.getBoundingClientRect();
  const size = 56;
  return {
    src: first.currentSrc || first.src,
    rect: new DOMRect(from.left + from.width / 2 - size / 2, from.top + from.height / 2 - size / 2, size, size),
  };
}

// Al volver con «atrás» desde el pago, la página puede restaurarse tal cual:
// «Comprar ahora» no debe quedar trabado.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('[data-buy-now][aria-busy]').forEach((button) => {
    button.removeAttribute('aria-busy');
    button.disabled = false;
  });
});

/* ---------- Barra de compra fija (mobile) ---------- */

class StickyBuy extends HTMLElement {
  connectedCallback() {
    const form = document.getElementById(this.dataset.form);
    const button = form && form.querySelector('[data-add-button]');
    if (!button || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(([entry]) => {
      this.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      // Avisa a la página para que los botones flotantes de apps (Chatty) suban sobre la barra.
      // (En desktop la barra no se muestra: offsetHeight es 0.)
      const root = document.documentElement;
      const height = this.hidden ? 0 : this.offsetHeight;
      root.classList.toggle('has-sticky-buy', height > 0);
      if (height > 0) root.style.setProperty('--sticky-buy-height', `${height}px`);
    });
    this.observer.observe(button);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    document.documentElement.classList.remove('has-sticky-buy');
  }
}
customElements.define('sticky-buy', StickyBuy);

/* ---------- Fundido de secciones al scrollear ----------
 * En <main data-reveal-sections> (la home), cada sección salvo la primera
 * aparece con un fundido largo al entrar en pantalla (ver critical.css). Lo que ya se ve al cargar
 * no se oculta, así que no hay parpadeo; con movimiento reducido no hace nada.
 */

function revealSections() {
  const main = document.querySelector('[data-reveal-sections]');
  if (!main || !('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return; // en el editor del tema, todo visible

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('reveal--hidden');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -15% 0px' }
  );

  [...main.querySelectorAll(':scope > .shopify-section')].slice(1).forEach((section) => {
    if (section.getBoundingClientRect().top < window.innerHeight) return;
    section.classList.add('reveal', 'reveal--hidden');
    observer.observe(section);
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', revealSections);
else revealSections();

/* ---------- /search sin búsqueda: foco en el buscador del header ----------
 * Solo en desktop con mouse: en mobile abriría el teclado sin que se pida.
 */

if (document.querySelector('[data-focus-header-search]') && window.matchMedia('(min-width: 990px) and (pointer: fine)').matches) {
  const input = document.getElementById('header-search');
  if (input) input.focus({ preventScroll: true });
}

/* ---------- Palabra que rota (titular de la home) ----------
 * Cambia la última palabra del titular en orden aleatorio, sin repetir hasta
 * pasar por todas, con un fundido lento. Se pausa fuera de pantalla o con la
 * pestaña oculta, y no rota con prefers-reduced-motion.
 */

class WordRotator extends HTMLElement {
  connectedCallback() {
    this.word = this.querySelector('[data-word]');
    this.words = (this.dataset.words || '').split('|').filter(Boolean);
    if (!this.word || this.words.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.deck = [];
    this.visible = true;
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => (this.visible = entry.isIntersecting));
      this.observer.observe(this);
    }
    this.timer = setInterval(() => this.next(), 3400);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    clearTimeout(this.swap);
    if (this.observer) this.observer.disconnect();
  }

  draw() {
    if (!this.deck.length) {
      const current = this.word.textContent.trim();
      this.deck = this.words.filter((word) => word !== current);
      for (let i = this.deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.deck[i], this.deck[j]] = [this.deck[j], this.deck[i]];
      }
    }
    return this.deck.pop();
  }

  next() {
    if (document.hidden || !this.visible) return;
    const word = this.draw();
    this.word.classList.add('is-leaving');
    this.swap = setTimeout(() => {
      this.word.textContent = word;
      this.word.classList.remove('is-leaving');
    }, 600);
  }
}
customElements.define('word-rotator', WordRotator);

/* ---------- Galería de la ficha ----------
 * La foto grande se desliza sola (scroll-snap). Esto solo conecta las
 * miniaturas: un toque lleva a su foto y la miniatura activa sigue al deslizar.
 */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-slides]');
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.counter = this.querySelector('[data-gallery-current]');
    if (!this.track || this.thumbs.length < 2) return;

    this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.show(index)));

    if (!('IntersectionObserver' in window)) return;
    const slides = [...this.track.children];
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.mark(slides.indexOf(entry.target));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  show(index) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    this.mark(index);
  }

  mark(index) {
    if (index < 0 || index === this.index) return;
    this.index = index;
    this.thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    if (this.counter) this.counter.textContent = index + 1;

    // En mobile las miniaturas son una tira: centrar la activa sin mover la página.
    const strip = this.thumbs[index].closest('ul');
    if (strip && strip.scrollWidth > strip.clientWidth) {
      const thumb = this.thumbs[index];
      strip.scrollTo({ left: thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
    }
  }
}
customElements.define('product-gallery', ProductGallery);

/* ---------- Fotos en grande (ficha) ----------
 * Tocar una foto de la galería la abre a pantalla completa en un <dialog>.
 * Se recorren deslizando (scroll-snap), con las flechas o con ← →; × o Esc
 * cierran (en desktop, un clic en el margen alrededor de la foto también) y
 * la galería queda en la última foto vista.
 *
 * Es circular: después de la última viene la primera (y al revés). Para que
 * también funcione deslizando, la tira lleva una copia de la última foto al
 * principio y de la primera al final; cuando el scroll se detiene en una
 * copia, salta sin animación a la foto real (son idénticas: no se nota).
 * Posición en la tira = foto real + 1.
 */

class ProductLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.track = this.querySelector('[data-lightbox-slides]');
    this.counter = this.querySelector('[data-lightbox-current]');
    this.count = this.track.children.length;
    this.index = 0;
    const gallery = this.previousElementSibling;
    this.gallery = gallery && gallery.matches('product-gallery') ? gallery : null;

    this.loop = this.count > 1;
    if (this.loop) {
      const slides = [...this.track.children];
      const clone = (slide) => {
        const copy = slide.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        return copy;
      };
      this.track.prepend(clone(slides[slides.length - 1]));
      this.track.append(clone(slides[0]));
    }

    (this.gallery || this.parentElement).querySelectorAll('[data-lightbox-open]').forEach((button) => {
      button.addEventListener('click', () => this.open(Number(button.dataset.lightboxOpen)));
    });
    this.querySelector('[data-lightbox-close]').addEventListener('click', () => this.dialog.close());
    this.querySelectorAll('[data-lightbox-step]').forEach((arrow) => {
      arrow.addEventListener('click', () => this.step(Number(arrow.dataset.lightboxStep)));
    });
    this.dialog.addEventListener('keydown', (event) => {
      const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
      if (!step) return;
      event.preventDefault();
      this.step(step);
    });
    // Un clic en el margen alrededor de la foto cierra.
    this.track.addEventListener('click', (event) => {
      if (!event.target.closest('img')) this.dialog.close();
    });
    this.track.addEventListener(
      'scroll',
      () => {
        if (!this.ticking) {
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.mark(this.position());
          });
        }
        // Sin scrollend (Safari viejo): un momento sin scroll cuenta como fin.
        if (!('onscrollend' in window)) {
          window.clearTimeout(this.settleTimer);
          this.settleTimer = window.setTimeout(() => this.settle(), 200);
        }
      },
      { passive: true }
    );
    // Al detenerse, si quedó en una copia, salta a la foto real.
    this.track.addEventListener('scrollend', () => this.settle());
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('has-lightbox');
      if (this.gallery && this.gallery.track) this.gallery.show(this.index);
    });
  }

  // Posición actual en la tira (con las copias).
  position() {
    return Math.round(this.track.scrollLeft / this.track.clientWidth);
  }

  scrollToPosition(position, smooth) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({
      left: position * this.track.clientWidth,
      behavior: smooth && !reduce ? 'smooth' : 'instant',
    });
  }

  open(index) {
    document.documentElement.classList.add('has-lightbox');
    this.dialog.showModal();
    this.scrollToPosition(this.loop ? index + 1 : index, false);
    this.mark(this.loop ? index + 1 : index);
  }

  // Una foto para adelante o para atrás; en los extremos pasa por la copia
  // (con animación) y settle() la lleva a la real.
  step(direction) {
    if (!this.loop) return;
    const target = Math.max(0, Math.min(this.count + 1, this.position() + direction));
    this.scrollToPosition(target, true);
    this.mark(target);
  }

  settle() {
    if (!this.loop) return;
    const position = this.position();
    if (position === 0) this.scrollToPosition(this.count, false);
    else if (position === this.count + 1) this.scrollToPosition(1, false);
  }

  mark(position) {
    const index = this.loop ? (position - 1 + this.count) % this.count : position;
    this.index = index;
    if (this.counter) this.counter.textContent = index + 1;
  }
}
customElements.define('product-lightbox', ProductLightbox);

/* ---------- Header al scrollear hacia arriba ----------
 * Celular (hasta 989 px): una barra compacta aparte. Aparece al subir (con un
 * margen, para que no tiemble) y se esconde al bajar, aunque el cursor esté en
 * el buscador: bajar es seguir mirando, así que se le saca el foco (cierra el
 * teclado) y se cierran las sugerencias. Nunca aparece mientras se ve el
 * header original, y no se mueve con un panel abierto.
 *
 * Desktop: sobra lugar, así que vuelve el header mismo, con su logo, buscador,
 * cuenta y carrito (y «Filtrar» en listados). Su sección es sticky (CSS en
 * sections/header.liquid) y acá se esconde o se muestra (is-hidden).
 *
 * En la home y los listados maneja también la píldora «↑ Arriba» de abajo:
 * aparece y se esconde junto con la barra o el header flotante.
 */

class ScrollHeader extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector('.site-header');
    this.lastY = window.scrollY;
    this.shown = false;
    this.holdUntil = 0;
    // Desktop: en vez de esta barra, el que se va y vuelve es el header mismo.
    // Se mueve su sección entera (la que lo envuelve), sticky por CSS.
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.frame = this.header && this.header.parentElement;
    if (this.frame) {
      this.frame.classList.add('site-header-frame');
      this.measure = () => document.documentElement.style.setProperty('--header-height', `${this.frame.offsetHeight}px`);
      this.measure();
      window.addEventListener('resize', this.measure, { passive: true });
    }
    this.desktop.addEventListener('change', () => this.reset());
    if (this.frame) this.showHeader(true);
    // Al enfocar en mobile, el teclado abre y el navegador scrollea solo: eso no cuenta.
    this.focusedAt = 0;
    this.addEventListener('focusin', () => (this.focusedAt = performance.now()));

    // «Filtrar» (en esta barra y, en desktop, en el header) solo si la página
    // tiene filtros; abre el mismo panel.
    const pageFilter = document.querySelector('facet-filters [data-open-facets]');
    const filterButtons = [
      [this.querySelector('[data-scroll-filter]'), 'scroll-header__count'],
      [this.header && this.header.querySelector('[data-header-filter]'), 'site-header__count'],
    ];
    filterButtons.forEach(([button, badgeClass]) => {
      if (!button || !pageFilter) return;
      const count = pageFilter.querySelector('.facets__count');
      if (count) button.append(Object.assign(document.createElement('span'), { className: badgeClass, textContent: count.textContent.trim() }));
      button.hidden = false;
      button.addEventListener('click', () => pageFilter.click());
    });

    this.backToTop = document.querySelector('[data-back-to-top]');
    if (this.backToTop) this.backToTop.addEventListener('click', (event) => this.goTop(event));

    // La fila de páginas es la del header: se copia, así no hay dos menús que mantener.
    this.nav = this.querySelector('[data-scroll-nav]');
    const sourceNav = this.header && this.header.querySelector('.site-header__nav ul');
    if (this.nav && sourceNav) {
      this.nav.append(sourceNav.cloneNode(true));
      this.nav.hidden = false;
    }

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
        this.syncBackToTop();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.measure) window.removeEventListener('resize', this.measure);
  }

  // Al cruzar 990 px (girar la tablet, achicar la ventana) se arranca de cero.
  reset() {
    this.show(false);
    this.classList.remove('is-handoff');
    this.showHeader(true);
    if (this.frame) this.frame.classList.remove('is-floating');
    this.shown = false;
    this.lastY = window.scrollY;
    this.syncBackToTop();
  }

  // Desktop: el header se va al bajar y vuelve al subir. Cerca del principio
  // (dentro de su propio alto) siempre se ve.
  updateDesktop() {
    const y = window.scrollY;
    const delta = y - this.lastY;
    const height = this.frame.offsetHeight;
    this.frame.classList.toggle('is-floating', y > height);
    if (performance.now() < this.holdUntil) {
      // Se queda a la vista, pero si se llegó arriba ya no cuenta como flotante.
      this.shown = !this.frame.classList.contains('is-hidden') && y > height;
      this.lastY = y;
      return;
    }
    if (y <= height) {
      this.showHeader(true);
      this.lastY = y;
      return;
    }
    if (document.querySelector('dialog[open]')) {
      this.lastY = y;
      return;
    }
    if (Math.abs(delta) < 10) return;
    if (delta > 0 && this.header.contains(document.activeElement)) document.activeElement.blur();
    this.showHeader(delta < 0);
    this.lastY = y;
  }

  showHeader(visible) {
    if (!this.frame) return;
    if (!visible) {
      const search = this.header.querySelector('predictive-search');
      if (search && search.results && !search.results.hidden) search.toggle(false);
    }
    this.frame.classList.toggle('is-hidden', !visible);
    this.frame.inert = !visible;
    document.documentElement.classList.toggle('has-header-shown', visible);
    // «Mostrado» para la píldora «↑ Arriba» y el carrito: flotando y a la vista.
    if (this.desktop.matches) this.shown = visible && this.frame.classList.contains('is-floating');
  }

  // Adónde vuela la tapa al agregar al carrito: el carrito del header si está a
  // la vista; si no, el de la barra (celular) o el del header (desktop), que
  // bajan para recibirla. Se apunta a donde van a quedar (pueden estar
  // moviéndose con transform).
  cartTarget() {
    const headerCart = this.header && this.header.querySelector('[data-cart-link]');
    const desktop = this.desktop.matches;
    if (headerCart && (desktop || !this.shown)) {
      const rect = headerCart.getBoundingClientRect();
      if (rect.top >= 0 && rect.bottom <= window.innerHeight) return rect;
    }
    this.reveal();
    const frame = desktop ? this.frame : this;
    const cart = desktop ? headerCart : this.querySelector('[data-cart-link]');
    if (!frame || !cart) return null;
    const rect = cart.getBoundingClientRect();
    return new DOMRect(rect.left, rect.top - frame.getBoundingClientRect().top, rect.width, rect.height);
  }

  update() {
    if (this.desktop.matches && this.frame) {
      this.updateDesktop();
      return;
    }
    const y = window.scrollY;
    const delta = y - this.lastY;
    const headerBottom = this.header ? this.header.getBoundingClientRect().bottom : Infinity;

    // Volviendo arriba: el header entra por debajo de la barra, que se queda
    // opaca y lo tapa. Cuando el header ya cubre todo el alto de la barra, las
    // dos filas de páginas (idénticas) coinciden: ahí la barra se desvanece en
    // el lugar, sin subir, y solo se apagan los íconos de arriba.
    // Recién agregado algo al carrito: se queda a la vista un momento.
    if (performance.now() < this.holdUntil) {
      this.lastY = y;
      return;
    }
    if (headerBottom > 0) {
      if (!this.shown || headerBottom >= this.offsetHeight) this.show(false, true);
      this.lastY = y;
      return;
    }
    // Lejos del header, la próxima vez que aparezca vuelve a bajar deslizándose.
    if (!this.shown) this.classList.remove('is-handoff');
    // Con un panel abierto (filtros, carrito) no se mueve.
    if (document.querySelector('dialog[open]')) {
      this.lastY = y;
      return;
    }
    if (Math.abs(delta) < 10) return;
    if (delta > 0 && this.contains(document.activeElement)) {
      if (performance.now() - this.focusedAt < 700) {
        this.lastY = y;
        return;
      }
      document.activeElement.blur();
    }
    this.show(delta < 0);
    this.lastY = y;
  }

  // handoff: se va desvaneciéndose en el lugar (el header ya ocupa su sitio).
  show(visible, handoff = false) {
    if (this.shown === visible) return;
    if (!visible) {
      // Si quedaron sugerencias abiertas sin foco, se cierran con la barra.
      const search = this.querySelector('predictive-search');
      if (search && search.results && !search.results.hidden) search.toggle(false);
    }
    if (visible) this.centerNav();
    if (!visible && handoff) this.syncNav();
    this.classList.toggle('is-handoff', !visible && handoff);
    this.shown = visible;
    this.classList.toggle('is-visible', visible);
    this.inert = !visible;
  }

  // Al agregar al carrito: baja (si no estaba) para que la tapa tenga dónde
  // aterrizar, y se queda unos segundos aunque se siga scrolleando.
  reveal(ms = 2500) {
    this.holdUntil = performance.now() + ms;
    if (this.desktop.matches && this.frame) {
      this.showHeader(true);
      this.syncBackToTop();
      return;
    }
    this.classList.remove('is-handoff');
    this.show(true);
    this.syncBackToTop();
  }

  // La página actual, centrada en la fila (sin animación: la barra recién aparece).
  centerNav() {
    const list = this.nav && this.nav.querySelector('ul');
    const active = list && list.querySelector('[aria-current="page"]');
    if (!active || list.scrollWidth <= list.clientWidth) return;
    const item = active.parentElement.getBoundingClientRect();
    const left = item.left - list.getBoundingClientRect().left + list.scrollLeft;
    list.scrollLeft = left - (list.clientWidth - item.width) / 2;
  }

  // Al devolverle el lugar al header, su fila queda deslizada igual que la de
  // la barra (por si la persona la movió), así no salta de costado.
  syncNav() {
    const list = this.nav && this.nav.querySelector('ul');
    const sourceList = this.header && this.header.querySelector('.site-header__nav ul');
    if (list && sourceList) sourceList.scrollLeft = list.scrollLeft;
  }

  syncBackToTop() {
    if (!this.backToTop) return;
    const visible = this.shown;
    this.backToTop.classList.toggle('is-visible', visible);
    this.backToTop.inert = !visible;
  }

  goTop(event) {
    event.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    // El foco vuelve al título del listado, para teclado y lectores de pantalla.
    const heading = document.querySelector('main h1');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  }
}
customElements.define('scroll-header', ScrollHeader);

/* ---------- Búsqueda predictiva ---------- */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (!this.hasAttribute('data-predictive')) return;
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.search__results');
    this.url = this.input.dataset.predictiveUrl;
    this.timer = null;
    this.controller = null;

    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(this.input.value.trim()), 220);
    });
    this.input.addEventListener('focus', () => {
      if (this.results.innerHTML.trim() && this.input.value.trim().length >= 2) this.toggle(true);
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.toggle(false);
    });
  }

  async search(terms) {
    if (terms.length < 2) {
      this.toggle(false);
      return;
    }
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const params = new URLSearchParams({
      q: terms,
      'resources[type]': 'product',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });
    try {
      const response = await fetch(`${this.url}?${params}`, { signal: this.controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      this.results.innerHTML = sectionHTML(await response.text(), '[data-predictive-results]');
      this.active = -1;
      this.toggle(true);
    } catch (error) {
      if (error.name !== 'AbortError') this.toggle(false);
    }
  }

  toggle(open) {
    this.results.hidden = !open;
    this.input.setAttribute('aria-expanded', String(open));
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.toggle(false);
      return;
    }
    if (this.results.hidden || !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
    const options = [...this.results.querySelectorAll('[role="option"]')];
    if (!options.length) return;
    if (event.key === 'Enter') {
      const selected = options[this.active];
      if (selected) {
        event.preventDefault();
        selected.querySelector('a').click();
      }
      return;
    }
    event.preventDefault();
    this.active = (this.active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
    options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.active)));
    this.input.setAttribute('aria-activedescendant', options[this.active].id);
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ---------- Filtros ---------- */

class FacetFilters extends HTMLElement {
  connectedCallback() {
    const dialog = this.querySelector('dialog');
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-open-facets]')) dialog.showModal();
      if (event.target.closest('[data-close-facets]') || event.target === dialog) dialog.close();
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-auto-submit]')) event.target.form.requestSubmit();
    });
  }
}
customElements.define('facet-filters', FacetFilters);

/* ---------- Recomendaciones ---------- */

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const load = async () => {
      try {
        const response = await fetch(this.dataset.url);
        const html = sectionHTML(await response.text(), 'product-recommendations');
        if (html.trim()) this.innerHTML = html;
      } catch (error) {
        /* sin recomendaciones: la sección queda vacía */
      }
    };
    if (!('IntersectionObserver' in window)) return load();
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(this);
  }
}
customElements.define('product-recommendations', ProductRecommendations);

/* ---------- Compartir ---------- */

class ShareButton extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector('button');
    const done = this.querySelector('[role="status"]');
    button.addEventListener('click', async () => {
      const url = button.dataset.shareUrl;
      if (navigator.share) {
        try {
          await navigator.share({ title: document.title, url });
        } catch (error) {
          /* el usuario canceló */
        }
        return;
      }
      await navigator.clipboard.writeText(url);
      done.hidden = false;
      setTimeout(() => (done.hidden = true), 2500);
    });
  }
}
customElements.define('share-button', ShareButton);

/* ---------- Cargar más (listados) ----------
 * Híbrido recomendado por Baymard: las primeras `data-auto-pages` páginas se
 * agregan solas al acercarse al final de la grilla; después, un botón. Las URLs
 * paginadas reales (?page=N) siguen existiendo debajo, para SEO y sin JS.
 * Al volver desde una ficha, reconstruye las páginas cargadas y el scroll.
 */

class LoadMore extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-load-more-grid]');
    this.button = this.querySelector('[data-load-more-button]');
    this.status = this.querySelector('[data-load-more-status]');
    this.live = this.querySelector('[data-load-more-live]');
    if (!this.grid || !this.button) return;

    this.total = Number(this.dataset.total);
    this.from = Number(this.dataset.from);
    this.shown = Number(this.dataset.shown);
    this.autoPages = Number(this.dataset.autoPages || 0);
    const saveData = navigator.connection && navigator.connection.saveData;
    if (saveData) this.autoPages = 0;
    this.loaded = 0;
    this.key = `load-more:${location.pathname}${location.search}`;
    this.renderStatus();

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.load({ focus: true });
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry.isIntersecting && this.loaded < this.autoPages) this.load();
      },
      { rootMargin: '0px 0px 800px 0px' }
    );
    this.observer.observe(this.button);

    window.addEventListener('pagehide', () => this.remember());
    this.restore();
  }

  get nextUrl() {
    return this.button && this.button.getAttribute('href');
  }

  async load({ focus = false } = {}) {
    if (this.busy || !this.nextUrl) return false;
    this.busy = true;
    this.button.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(this.nextUrl, location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url);
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = [...doc.querySelectorAll('[data-load-more-grid] > li')];
      const firstNew = items[0];
      this.grid.append(...items);
      this.loaded += 1;
      this.shown += items.length;

      const next = doc.querySelector('[data-load-more-button]');
      if (next) {
        this.button.setAttribute('href', next.getAttribute('href'));
        // Reiniciar la observación: si el botón sigue a la vista, cuenta como nueva entrada.
        this.observer.unobserve(this.button);
        this.observer.observe(this.button);
      } else {
        this.button.remove();
        this.button = null;
        this.observer.disconnect();
      }
      const pagination = doc.querySelector('.pagination');
      const current = this.querySelector('.pagination');
      if (pagination && current) current.replaceWith(pagination);

      this.renderStatus();
      if (this.live) this.live.textContent = `Se agregaron ${items.length} discos.`;
      if (focus && firstNew) {
        const link = firstNew.querySelector('a');
        if (link) link.focus({ preventScroll: false });
      }
      return true;
    } catch (error) {
      /* si falla, el botón sigue siendo un link a la página siguiente */
      return false;
    } finally {
      this.busy = false;
      if (this.button) this.button.removeAttribute('aria-busy');
    }
  }

  renderStatus() {
    if (!this.status) return;
    const format = (n) => n.toLocaleString('es-UY');
    const to = this.from + this.shown - 1;
    this.status.textContent =
      this.from > 1
        ? `Viendo del ${format(this.from)} al ${format(to)} de ${format(this.total)}`
        : `Viendo ${format(to)} de ${format(this.total)}`;
  }

  remember() {
    try {
      sessionStorage.setItem(this.key, JSON.stringify({ loaded: this.loaded, y: window.scrollY }));
    } catch (error) {
      /* sin sessionStorage no se recuerda la posición */
    }
  }

  async restore() {
    const nav = performance.getEntriesByType('navigation')[0];
    if (!nav || nav.type !== 'back_forward') return;
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.key) || 'null');
    } catch (error) {
      saved = null;
    }
    if (!saved || !saved.loaded) return;
    for (let i = 0; i < saved.loaded; i += 1) {
      if (!(await this.load())) break;
    }
    window.scrollTo(0, saved.y);
  }
}
customElements.define('load-more', LoadMore);
